import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Button, Input } from '@follow/ui';
import { ClipboardIcon } from '@heroicons/react/24/outline';

import AssetsService from '../../services/AssetsService';
import ModalConfirmDelete from '../../components/Modals/ModalConfirmDelete';
import useStation from '../../hooks/useStation';
import useLocationRole from '../../hooks/useLocationRole';

const StationSettings = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();
  const queryClient = useQueryClient();

  const { station, stationIndex } = useStation(siteId, stationId);

  // The active/inactive toggle only matters when rental is enabled — the flag
  // gates rental availability, so hide it entirely for monitoring-only accounts.
  // Read per-location: a teammate's rentals permission is scoped to one site.
  // Renaming / (de)activating a station is owner-only.
  const { rentalsEnabled: rentalEnabled, canManage, isLoading: roleLoading } = useLocationRole(siteId);

  const [name, setName] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  useEffect(() => {
    if (station && !isDirty) {
      setName(station.name || `Station #${station.id}`);
    }
  }, [station, stationIndex, isDirty]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setIsDirty(true);
  };

  const handleCancel = () => {
    if (station) {
      setName(station.name || `Station #${station.id}`);
      setIsDirty(false);
    }
  };

  const updateStationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string } }) =>
      AssetsService.updateStation(id, data),
    onSuccess: () => {
      toast.success('Station mise à jour');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['location', siteId] });
      queryClient.invalidateQueries({ queryKey: ['stations', siteId] });
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour');
    },
  });

  const invalidateStationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['location', siteId] });
    queryClient.invalidateQueries({ queryKey: ['stations', siteId] });
  };

  const deactivateStationMutation = useMutation({
    mutationFn: (id: number) => AssetsService.deactivateStation(id),
    onSuccess: () => {
      toast.success('Station désactivée');
      invalidateStationQueries();
    },
    onError: () => {
      toast.error('Erreur lors de la désactivation');
    },
    onSettled: () => {
      setShowDeactivateModal(false);
    },
  });

  const activateStationMutation = useMutation({
    mutationFn: (id: number) => AssetsService.activateStation(id),
    onSuccess: () => {
      toast.success('Station réactivée');
      invalidateStationQueries();
    },
    onError: () => {
      toast.error('Erreur lors de la réactivation');
    },
  });

  const handleSave = () => {
    if (!stationId) return;
    updateStationMutation.mutate({ id: Number(stationId), data: { name } });
  };

  const handleCopyToken = () => {
    if (station?.token) {
      navigator.clipboard.writeText(station.token).then(() => {
        toast.success('Token copié');
      }).catch(() => {
        toast.error('Impossible de copier');
      });
    }
  };

  const handleDeactivate = () => {
    if (!stationId || !station) return;
    deactivateStationMutation.mutate(Number(stationId));
  };

  const handleActivate = () => {
    if (!stationId || !station) return;
    activateStationMutation.mutate(Number(stationId));
  };

  // Placed after every hook so the hook order stays stable across renders.
  // Waits for the access block: deciding while it is undefined would bounce an
  // owner off their own settings page.
  if (!roleLoading && !canManage) {
    return <Navigate to={`/site/${siteId}/station/${stationId}/operations`} replace />;
  }

  return (
    <>
      <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-8">
        <div className="self-stretch inline-flex justify-start items-start gap-4">
          <div className="flex-1 inline-flex flex-col justify-center items-start">
            <h1 className="text-neutral typo-h1">Paramètres de la station</h1>
          </div>
        </div>
      </div>
      <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-4">
        <h2 className="text-neutral typo-h2">Détails</h2>
        <div className="self-stretch p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col justify-center items-start gap-6 overflow-hidden">
          <div className="w-full max-w-[480px] flex flex-col justify-start items-start gap-1">
            <label className="self-stretch text-neutral-80 typo-label">
              Nom de la station
            </label>
            <Input
              tone="dark"
              type="text"
              value={name}
              onChange={handleNameChange}
              disabled={!canManage}
            />
          </div>
          <div className="w-full max-w-[480px] relative flex flex-col justify-start items-start gap-1">
            <label className="self-stretch text-neutral-80 typo-label">
              Token de la station
            </label>
            <div className="self-stretch flex items-center gap-2">
              <div className="flex-1 h-9 px-3 opacity-25 bg-neutral-5 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex items-center">
                <span className="text-neutral typo-label truncate">
                  {station?.token ?? '...'}
                </span>
              </div>
              <button
                type="button"
                className="h-7 px-2 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex justify-center items-center gap-1 hover:bg-neutral-5 transition-colors"
                onClick={handleCopyToken}
              >
                <ClipboardIcon className="w-3.5 h-3.5 text-neutral-80" />
                <span className="text-center text-neutral-80 typo-label">Copier</span>
              </button>
            </div>
          </div>
          {canManage && (
            <div className="self-stretch inline-flex justify-end items-start gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Annuler
              </Button>
              <Button variant="primary" tone="light" size="sm" onClick={handleSave} disabled={!isDirty}>
                Sauvegarder
              </Button>
            </div>
          )}
        </div>
      </div>
      {canManage && rentalEnabled && station && (station.active ? (
        <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-4">
          <h2 className="text-neutral typo-h2">Danger zone</h2>
          <div className="self-stretch p-4 bg-error-100 rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-error-500 flex flex-col justify-center items-start gap-6 overflow-hidden">
            <div className="self-stretch flex flex-col justify-start items-start">
              <span className="text-neutral typo-label">
                Désactiver cette station
              </span>
              <span className="self-stretch text-neutral-80 typo-small">
                La station ne sera plus disponible à la location. Elle continuera d'envoyer ses données de monitoring (voltage, signal…) et pourra être réactivée à tout moment.
              </span>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeactivateModal(true)}
              disabled={deactivateStationMutation.isPending}
            >
              Désactiver cette station
            </Button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-[640px] px-4 flex flex-col justify-start items-start gap-4">
          <h2 className="text-neutral typo-h2">Activation</h2>
          <div className="self-stretch p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col justify-center items-start gap-6 overflow-hidden">
            <div className="self-stretch flex flex-col justify-start items-start">
              <span className="text-neutral typo-label">
                Cette station est inactive
              </span>
              <span className="self-stretch text-neutral-80 typo-small">
                Elle n'est pas disponible à la location. Réactivez-la pour la rendre à nouveau réservable.
              </span>
            </div>
            <Button
              variant="primary"
              tone="light"
              size="sm"
              onClick={handleActivate}
              disabled={activateStationMutation.isPending}
            >
              Réactiver cette station
            </Button>
          </div>
        </div>
      ))}
      <ModalConfirmDelete
        visible={showDeactivateModal}
        title="Désactiver cette station"
        description="La station ne sera plus disponible à la location, mais continuera d'envoyer ses données de monitoring. Vous pourrez la réactiver à tout moment."
        confirmLabel="Désactiver"
        onCancel={() => setShowDeactivateModal(false)}
        onConfirm={handleDeactivate}
      />
    </>
  );
};

export default StationSettings;
